import { useCallback } from 'react'
import { ICellRendererParams } from 'ag-grid-community'
import { ProgressReportTableContent } from '../table/ProgressReportTable'
import {
  getProjectPlanPath,
  getWbsItemUuidByProjectPlanUuid,
} from '../../../../../lib/functions/projectPlan'
import TicketListApi from '../../../../../lib/functions/ticketList'
import store from '../../../../../store'
import { addGlobalMessage, MessageLevel } from '../../../../../store/messages'
import { intl } from '../../../../../i18n'
import { DiscussionTargetType } from '../../../../../domain/entity/DiscussionEntity'
import '../../../../../styles/agGrid.scss'

type ScopeRowNumberCellRendererProps =
  ICellRendererParams<ProgressReportTableContent> & {
    /** 行が工程かチケット管理表か。移り先が無いときに出す警告の文言を、これで振り分ける */
    targetType?: DiscussionTargetType
  }

const buildProcessUrl = async (
  projectCode: string,
  projectPlanUuid: string
): Promise<string> => {
  // 工程が消えていたら例外になり、移る前に警告できる
  await getProjectPlanPath(projectPlanUuid)
  return `${window.location.origin}/projectPlan/${projectCode}?treeRootUuid=${projectPlanUuid}`
}

// チケット管理表の行は、管理表のチケットを一覧で見られるよう、プロジェクト計画ではなくチケット管理表の画面を開く
const buildTicketListUrl = async (
  projectCode: string,
  projectPlanUuid: string
): Promise<string> => {
  const wbsItemUuid = await getWbsItemUuidByProjectPlanUuid(projectPlanUuid)
  const ticketListUuid = (await TicketListApi.getUuidByWbsItemUuid(wbsItemUuid))
    .json
  // スナップショットを撮ったあとに管理表が消えると、エラーにはならず空が返る。
  // そのまま URL に埋めると ticketList=undefined のタブが開いてしまうので、
  // 工程が消えていたときと同じく例外にして警告に合流させる
  if (!ticketListUuid) {
    throw new Error(`ticket list not found. wbsItemUuid: ${wbsItemUuid}`)
  }
  return `${window.location.origin}/tickets/${projectCode}?ticketList=${ticketListUuid}`
}

export const ScopeRowNumberCellRenderer = ({
  data,
  value,
  targetType,
}: ScopeRowNumberCellRendererProps) => {
  const processUuid = data?.uuid

  const handleClick = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation()
      if (!processUuid) return

      const projectCode = store.getState().project.current?.code
      if (!projectCode) return

      try {
        const url =
          targetType === 'TICKET_LIST'
            ? await buildTicketListUrl(projectCode, processUuid)
            : await buildProcessUrl(projectCode, processUuid)
        window.open(url, '_blank')
      } catch {
        store.dispatch(
          addGlobalMessage({
            type: MessageLevel.WARN,
            title: intl.formatMessage({
              id:
                targetType === 'TICKET_LIST'
                  ? 'versionReport.progressReport.ticketListNotFound'
                  : 'versionReport.progressReport.process.notFound',
            }),
          })
        )
      }
    },
    [processUuid, targetType]
  )

  return (
    <div className="sevend-ag-cell-sequence-no">
      {processUuid ? (
        <a className="sevend-ag-cell-sequence-no__link" onClick={handleClick}>
          {value}
        </a>
      ) : (
        <p className="sevend-ag-cell-sequence-no__label">{value}</p>
      )}
    </div>
  )
}
